import { ref, computed, reactive } from 'vue'
import apiClient from '../api/client.js'

// Estado compartilhado (singleton de módulo, mesmo padrão de useDarkMode.js)
// — Navbar.vue (sino geral) e TopBar.vue (sino só de novidades) consomem a
// mesma lista/polling em vez de cada um buscar por conta própria.
//
// Backend: src/routes/notifications.js. Gatilhos hoje (ver
// src/utils/notifications.js): solicitação de suporte/exclusão de conta
// criada pelo tenant avisa o platform owner; resposta/rejeição do owner
// avisa de volta o tenant; nova versão publicada avisa todo o staff
// (notification.link === '/novidades').
const notifications = ref([])
const unreadCount = computed(() => notifications.value.filter((n) => !n.read_at).length)

// /novidades é owner-only (rota + API) — tenant não navega pra lá. A
// notificação de nova versão já carrega o título e o corpo completos (ver
// notifyAllStaff em src/utils/notifications.js), então pra quem não é owner
// o clique abre esse modal local em vez de tentar navegar (ver markAsRead).
const releaseDetailModal = reactive({ open: false, title: '', version: '', notes: '', date: '' })

const formatReleaseDate = (v) =>
  new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })

// notification.title é sempre o texto fixo "Nova versão disponível" (ver
// RELEASE_TITLE em src/routes/releases.js). A versão vem na primeira linha
// de notification.message ("v{versão}"); separamos essa linha do resto
// (as notas) pra mostrar a versão como badge no modal.
const parseVersionLine = (line) => line.trim().replace(/^v/, '')

const releaseNotesLines = computed(() =>
  (releaseDetailModal.notes || '').split('\n').map((l) => l.trim().replace(/^[-*]\s*/, '')).filter(Boolean)
)

async function loadNotifications() {
  try {
    const { data } = await apiClient.get('/notifications')
    notifications.value = data.data
  } catch {
    // Best-effort — não interrompe o resto da navbar/topbar se a listagem falhar.
  }
}

// Vários componentes podem estar "assinando" o polling ao mesmo tempo
// (Navbar + TopBar); só o primeiro a montar liga o setInterval, só o
// último a desmontar desliga — evita duas chamadas a cada 45s.
let poll = null
let subscribers = 0
function startPolling() {
  subscribers++
  if (poll) return
  loadNotifications()
  poll = setInterval(loadNotifications, 45000)
}
function stopPolling() {
  subscribers = Math.max(0, subscribers - 1)
  if (subscribers === 0 && poll) {
    clearInterval(poll)
    poll = null
  }
}

// currentUserRole/router vêm de quem chama (Navbar.vue/TopBar.vue já têm
// authStore/useRouter no próprio setup) — evita a composable depender de
// Pinia/vue-router estarem ativos no momento em que o módulo é importado.
async function markAsRead(notification, currentUserRole, router) {
  if (!notification.read_at) {
    notification.read_at = new Date().toISOString()
    try {
      await apiClient.post(`/notifications/${notification.id}/read`)
    } catch {
      notification.read_at = null
    }
  }

  const isVersionNotification = notification.link === '/novidades'
  if (isVersionNotification && currentUserRole !== 'PLATFORM_OWNER') {
    const newlineIndex = notification.message.indexOf('\n')
    const versionLine = newlineIndex === -1 ? notification.message : notification.message.slice(0, newlineIndex)
    const notes = newlineIndex === -1 ? '' : notification.message.slice(newlineIndex + 1).trim()
    releaseDetailModal.title = notification.title
    releaseDetailModal.version = parseVersionLine(versionLine)
    releaseDetailModal.notes = notes
    releaseDetailModal.date = formatReleaseDate(notification.created_at)
    releaseDetailModal.open = true
    return
  }

  if (notification.link) router.push(notification.link)
}

async function markAllAsRead() {
  const previouslyUnread = notifications.value.filter((n) => !n.read_at)
  const now = new Date().toISOString()
  previouslyUnread.forEach((n) => { n.read_at = now })
  try {
    await apiClient.post('/notifications/read-all')
  } catch {
    previouslyUnread.forEach((n) => { n.read_at = null })
  }
}

export function useNotifications() {
  return {
    notifications,
    unreadCount,
    releaseDetailModal,
    releaseNotesLines,
    loadNotifications,
    startPolling,
    stopPolling,
    markAsRead,
    markAllAsRead,
  }
}
